import React, { useCallback, useEffect, useRef, useState } from 'react';
import CustomersNoticeFmtBar from '../../users-customers/components/CustomersNoticeFmtBar';
import CustomersNoticeOryaCompose from '../../users-customers/components/CustomersNoticeOryaCompose';
import { aiWriteOsNote } from '../services/osService';

export const OSV2_ORYA_TEXTAREA_SELECTOR = '.oosv2-obs-textarea, .oosv2-agenda-notes';

const COMPOSE_STEPS = [
  { text: 'Orya está pensando…', duration: 2000 },
  { text: 'Escrevendo a observação…', duration: 2500 },
  { text: 'Ajustando o tom…', duration: 2500 },
  { text: 'Gerando resposta…', duration: null },
];

function caretOnEmptyLine(el) {
  const { value, selectionStart, selectionEnd } = el;
  if (selectionStart !== selectionEnd) return false;
  const before = value.slice(0, selectionStart);
  const after = value.slice(selectionStart);
  const lineStart = before.lastIndexOf('\n') + 1;
  const nl = after.indexOf('\n');
  const lineEnd = nl === -1 ? value.length : selectionStart + nl;
  return value.slice(lineStart, lineEnd).trim() === '';
}

/** Atualiza textarea controlado pelo React (dispara onChange). */
function setTextareaValue(el, value, caret) {
  const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;
  setter.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  requestAnimationFrame(() => {
    if (!el.isConnected) return;
    el.focus({ preventScroll: true });
    if (caret != null) el.setSelectionRange(caret, caret);
  });
}

function spliceText(el, start, end, text) {
  const value = el.value;
  const next = value.slice(0, start) + text + value.slice(end);
  setTextareaValue(el, next, start + text.length);
}

/**
 * Orya nos textareas de observação do Abrir OS v2 (mesma UX do editor de avisos):
 * espaço em linha vazia abre o compose; selecionar texto mostra "Melhorar" e "Edit".
 */
export default function OpenOsV2OryaTextareaAssist({ hostRef, active }) {
  const [compose, setCompose] = useState(null);
  const [fmt, setFmt] = useState(null);
  const pointerRef = useRef({ x: null });
  const composeRef = useRef(null);
  composeRef.current = compose;

  const targetFrom = useCallback(
    (node) => {
      if (!(node instanceof Element)) return null;
      const el = node.closest(OSV2_ORYA_TEXTAREA_SELECTOR);
      if (!el || el.tagName !== 'TEXTAREA' || !hostRef.current?.contains(el)) return null;
      return el;
    },
    [hostRef]
  );

  const syncSelection = useCallback((el) => {
    requestAnimationFrame(() => {
      if (!el.isConnected || document.activeElement !== el) return;
      const { selectionStart: start, selectionEnd: end, value } = el;
      const text = value.slice(start, end).trim();
      if (start === end || !text) {
        setFmt((prev) => (prev && prev.el === el ? null : prev));
        return;
      }
      const r = el.getBoundingClientRect();
      const barW = 170;
      const x = pointerRef.current.x ?? r.left + 12;
      setFmt({
        el,
        start,
        end,
        text,
        top: Math.max(8, r.top - 40),
        left: Math.max(12, Math.min(x - barW / 2, window.innerWidth - barW - 12)),
      });
    });
  }, []);

  useEffect(() => {
    if (!active) {
      setCompose(null);
      setFmt(null);
      return undefined;
    }
    const onKeyDown = (e) => {
      const el = targetFrom(e.target);
      if (!el) return;
      if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (composeRef.current || !caretOnEmptyLine(el)) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setFmt(null);
      setCompose({
        el,
        start: el.selectionStart,
        end: el.selectionEnd,
        top: r.top + Math.min(r.height, 36),
        left: r.left + 8,
      });
    };
    const onMouseUp = (e) => {
      const el = targetFrom(e.target);
      if (!el) return;
      pointerRef.current.x = e.clientX;
      syncSelection(el);
    };
    const onKeyUp = (e) => {
      const el = targetFrom(e.target);
      if (!el) return;
      pointerRef.current.x = null;
      syncSelection(el);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mouseup', onMouseUp, true);
    document.addEventListener('keyup', onKeyUp, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mouseup', onMouseUp, true);
      document.removeEventListener('keyup', onKeyUp, true);
    };
  }, [active, targetFrom, syncSelection]);

  const contextFor = (el) => String(el?.getAttribute('data-orya-context') || '').trim();

  const closeCompose = useCallback(() => {
    const c = composeRef.current;
    setCompose(null);
    if (c?.el?.isConnected) {
      requestAnimationFrame(() => {
        c.el.focus({ preventScroll: true });
        c.el.setSelectionRange(c.start, c.end);
      });
    }
  }, []);

  if (!active) return null;

  return (
    <>
      {compose ? (
        <CustomersNoticeOryaCompose
          position={{ top: compose.top, left: compose.left }}
          keepOpenSelector={OSV2_ORYA_TEXTAREA_SELECTOR}
          aiWrite={aiWriteOsNote}
          thinkingSteps={COMPOSE_STEPS}
          contextDraft={[contextFor(compose.el), compose.el.value.trim() && `Texto atual:\n${compose.el.value.trim()}`]
            .filter(Boolean)
            .join('\n\n')}
          onInsert={(text) => {
            const t = String(text || '').trim();
            if (t && compose.el.isConnected) spliceText(compose.el, compose.start, compose.end, t);
            composeRef.current = null;
            setCompose(null);
          }}
          onClose={closeCompose}
        />
      ) : null}
      {fmt ? (
        <CustomersNoticeFmtBar
          key={`${fmt.start}-${fmt.end}`}
          aiOnly
          position={{ top: fmt.top, left: fmt.left }}
          selectedText={fmt.text}
          keepOpenSelector={OSV2_ORYA_TEXTAREA_SELECTOR}
          aiWrite={aiWriteOsNote}
          onReplaceSelection={(text) => {
            const t = String(text || '').trim();
            if (t && fmt.el.isConnected) spliceText(fmt.el, fmt.start, fmt.end, t);
          }}
          onInsertBelow={(text) => {
            const t = String(text || '').trim();
            if (t && fmt.el.isConnected) spliceText(fmt.el, fmt.end, fmt.end, `\n${t}`);
          }}
          onClose={() => setFmt(null)}
        />
      ) : null}
    </>
  );
}
